import { Fragment } from "react";
import { VerificationNames } from "shared/types";
import { Cell } from "./Cell";
import { WithIcon } from "./WithIcon";
import { VERIFICATION_ICON, VERIFICATIONS_ORDER } from "../constants";
import { sortVerifications, verificationNameToTitle } from "../utils";
import { Verification } from "../types";
import { combineVerifications } from "shared/utils";
import { VerificationSchemaNames } from "types/landlord/verificationTemplate";

const VerificationHeaderCell = ({
  v,
  verificationSchemas,
  hasClearaCategorization
}: {
  v: Verification;
  verificationSchemas?: VerificationSchemaNames;
  hasClearaCategorization: boolean;
}) => {
  if (v.name === VerificationNames.RentalApplication)
    return (
      <Fragment>
        <Cell className="bg-background-900">
          <WithIcon
            Icon={VERIFICATION_ICON[v.name as VerificationNames]}
            className="stroke-content-100"
          >
            <p className="text-content-300">
              {verificationNameToTitle(v.name as VerificationNames)}
            </p>
          </WithIcon>
        </Cell>
        <Cell isDataCell>Dependents</Cell>
        <Cell isDataCell>Vehicles</Cell>
        <Cell isDataCell>Pets or service animals</Cell>
      </Fragment>
    );

  if (v.name === VerificationNames.HousingHistory)
    return (
      <Fragment>
        <Cell className="bg-background-900">
          <WithIcon Icon={VERIFICATION_ICON[v.name as VerificationNames]}>
            {verificationNameToTitle(v.name as VerificationNames)}
          </WithIcon>
        </Cell>
        <Cell isDataCell>Number of residences</Cell>
        <Cell isDataCell>Average monthly payments</Cell>
        <Cell isDataCell>Total months of residence</Cell>
        {verificationSchemas?.rental_verification ? (
          <Cell isDataCell>Landlord verification</Cell>
        ) : null}
      </Fragment>
    );

  if (v.name === VerificationNames.PetScreeningReport)
    return (
      <Fragment>
        <Cell className="bg-background-900">
          <WithIcon Icon={VERIFICATION_ICON[v.name as VerificationNames]}>
            {verificationNameToTitle(v.name as VerificationNames)}
          </WithIcon>
        </Cell>
        <Cell isDataCell>Pets / Assistance animals</Cell>
      </Fragment>
    );

  if (v.name === VerificationNames.IdentityVerification)
    return (
      <Fragment>
        <Cell className="bg-background-900">
          <WithIcon
            Icon={VERIFICATION_ICON[v.name as VerificationNames]}
            className="stroke-content-100"
          >
            <p className="text-content-300">
              {verificationNameToTitle(v.name as VerificationNames)}
            </p>
          </WithIcon>
        </Cell>
        <Cell isDataCell>Identity verification</Cell>
      </Fragment>
    );

  if (v.name === VerificationNames.CreditReport)
    return (
      <Fragment>
        <Cell className="bg-background-900">
          <WithIcon Icon={VERIFICATION_ICON[v.name as VerificationNames]}>
            {verificationNameToTitle(v.name as VerificationNames)}
          </WithIcon>
        </Cell>
        <Cell
          isDataCell
          className="flex items-center pl-0 pr-0 relative"
          isFirst
        >
          Credit score
        </Cell>
        <Cell isDataCell className="flex items-center pl-0 pr-0 relative">
          Past due accounts
        </Cell>
        <Cell isDataCell className="flex items-center pl-0 pr-0 relative">
          Balance past due
        </Cell>
        <Cell isDataCell className="flex items-center pl-0 pr-0 relative">
          Balance of collections
        </Cell>
        <Cell isDataCell className="flex items-center pl-0 pr-0 relative">
          Bankruptcy
        </Cell>
      </Fragment>
    );

  if (v.name === VerificationNames.EvictionReport)
    return (
      <Fragment>
        <Cell className="bg-background-900">
          <WithIcon Icon={VERIFICATION_ICON[v.name as VerificationNames]}>
            {verificationNameToTitle(v.name as VerificationNames)}
          </WithIcon>
        </Cell>
        <Cell
          isDataCell
          className="flex items-center pl-0 pr-0 relative"
          isFirst
        >
          Events reported
        </Cell>
      </Fragment>
    );

  if (v.name === VerificationNames.CriminalReport)
    return (
      <Fragment>
        <Cell className="bg-background-900">
          <WithIcon Icon={VERIFICATION_ICON[v.name as VerificationNames]}>
            {verificationNameToTitle(v.name as VerificationNames)}
          </WithIcon>
        </Cell>
        {hasClearaCategorization ? (
          <>
            <Cell isDataCell>Felony</Cell>
            <Cell isDataCell>Misdemeanor</Cell>
            <Cell isDataCell>Other criminal</Cell>
          </>
        ) : (
          <>
            <Cell isDataCell>Criminal</Cell>
            <Cell isDataCell>Criminal traffic</Cell>
          </>
        )}
        <Cell isDataCell>National sex offender </Cell>
        <Cell isDataCell>OFAC</Cell>
      </Fragment>
    );

  if (v.name === VerificationNames.CanadianCriminalReport)
    return (
      <Fragment>
        <Cell className="bg-background-900">
          <WithIcon Icon={VERIFICATION_ICON[v.name as VerificationNames]}>
            {verificationNameToTitle(v.name as VerificationNames)}
          </WithIcon>
        </Cell>
        <Cell isDataCell>Report</Cell>
      </Fragment>
    );

  return null;
};

export const VerificationHeaderCells = ({
  availableVerificationNames,
  verificationSchemas,
  hasClearaCategorization
}: {
  availableVerificationNames: VerificationNames[];
  verificationSchemas?: VerificationSchemaNames;
  hasClearaCategorization: boolean;
}) => {
  const verifications = combineVerifications(
    sortVerifications(
      availableVerificationNames.map((name) => ({
        id: name,
        name: name as VerificationNames
      })) as unknown[] as Verification[],
      VERIFICATIONS_ORDER
    ),
    [
      [
        VerificationNames.IncomeVerification,
        VerificationNames.VerifiedIncomeVerification,
        VerificationNames.CombinedIncome
      ]
    ]
  );

  return (
    <>
      {verifications.map((v) => {
        if (Array.isArray(v))
          return (
            <Fragment key={v[0].id}>
              <Cell className="bg-background-900">
                <WithIcon
                  Icon={VERIFICATION_ICON[VerificationNames.IncomeVerification]}
                  className="stroke-content-100"
                >
                  <p className="text-content-300">Income history</p>
                </WithIcon>
              </Cell>
              <Cell isDataCell>
                <p>
                  Gross income
                  <span className="text-content-100"> (Verified)</span>
                </p>
              </Cell>
              <Cell isDataCell>
                <p>
                  Net income
                  <span className="text-content-100"> (Verified)</span>
                </p>
              </Cell>
              <Cell isDataCell>
                <p>
                  Gross income
                  <span className="text-content-100"> (Self-reported)</span>
                </p>
              </Cell>
            </Fragment>
          );

        return (
          <VerificationHeaderCell
            key={v.id}
            v={v}
            verificationSchemas={verificationSchemas}
            hasClearaCategorization={hasClearaCategorization}
          />
        );
      })}
    </>
  );
};
